/**
 * @file
 * Smooth document scrolling (Lenis) + distance-based hash/anchor scrolls.
 *
 * Native CSS `scroll-behavior` does not smooth wheel/trackpad scrolling.
 * Lenis handles continuous body scroll; hash links and Drupal.neo.animateScrollTo
 * use the same ease-in-out cubic with a distance-based duration.
 */
((Drupal, once) => {
  /** Floor so short jumps still read as a scroll, not a snap. */
  const MIN_DURATION_MS = 900;
  /** Cap so long pages do not crawl. */
  const MAX_DURATION_MS = 1800;
  /** Pixels per millisecond between min and max. */
  const PX_PER_MS = 0.9;
  /** Body class that locks page scroll (estate filters sheet). */
  const SCROLL_LOCK_CLASS = 'estate-filters-modal-open';

  let rafId = 0;
  /** @type {InstanceType<typeof Lenis>|null} */
  let lenis = null;

  /**
   * @return {boolean}
   */
  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * @param {string} path
   *
   * @return {string}
   */
  const normalizePath = (path) => path.replace(/\/+$/, '') || '/';

  /**
   * Ease-in-out cubic.
   *
   * @param {number} t
   *   Linear progress 0–1.
   *
   * @return {number}
   */
  const easeInOutCubic = (t) =>
    t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

  /**
   * @param {number} distance
   *
   * @return {number}
   *   Duration in seconds for Lenis / ms for the RAF fallback.
   */
  const durationForDistance = (distance) =>
    Math.min(
      MAX_DURATION_MS,
      Math.max(MIN_DURATION_MS, Math.abs(distance) / PX_PER_MS),
    );

  /**
   * @return {void}
   */
  const cancelAnimation = () => {
    if (rafId) {
      window.cancelAnimationFrame(rafId);
      rafId = 0;
    }
  };

  /**
   * Document Y that lands the element under its CSS scroll-margin.
   *
   * @param {HTMLElement|null} element
   *
   * @return {number}
   */
  const getTargetY = (element) => {
    if (!element) {
      return 0;
    }
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);
    const scrollMargin =
      parseFloat(style.scrollMarginTop) ||
      parseFloat(style.scrollMarginBlockStart) ||
      0;
    const scrollY = lenis ? lenis.scroll : window.scrollY;
    return Math.max(0, scrollY + rect.top - scrollMargin);
  };

  /**
   * Animate the document (body) scroll to a Y position.
   *
   * @param {number} targetY
   * @param {Function} [onComplete]
   */
  const animateScrollTo = (targetY, onComplete) => {
    cancelAnimation();

    const startY = lenis ? lenis.scroll : window.scrollY;
    const distance = targetY - startY;
    if (Math.abs(distance) < 1) {
      if (onComplete) {
        onComplete();
      }
      return;
    }

    if (prefersReducedMotion()) {
      if (lenis) {
        lenis.scrollTo(targetY, { immediate: true, force: true });
      } else {
        window.scrollTo(window.scrollX, targetY);
      }
      if (onComplete) {
        onComplete();
      }
      return;
    }

    const durationMs = durationForDistance(distance);

    if (lenis) {
      lenis.scrollTo(targetY, {
        // Prefer duration/easing over the wheel lerp for programmatic jumps.
        lerp: undefined,
        duration: durationMs / 1000,
        easing: easeInOutCubic,
        force: true,
        lock: true,
        onComplete: () => {
          if (onComplete) {
            onComplete();
          }
        },
      });
      return;
    }

    const startTime = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - startTime) / durationMs);
      window.scrollTo(window.scrollX, startY + distance * easeInOutCubic(t));
      if (t < 1) {
        rafId = window.requestAnimationFrame(step);
      } else {
        rafId = 0;
        if (onComplete) {
          onComplete();
        }
      }
    };

    rafId = window.requestAnimationFrame(step);
  };

  /**
   * @param {string} hash
   *   Location hash including `#`, or empty for the top of the page.
   *
   * @return {HTMLElement|null}
   *   Target element, or null to scroll to Y=0.
   */
  const resolveTarget = (hash) => {
    const id = hash ? decodeURIComponent(hash.replace(/^#/, '')) : '';
    if (!id) {
      return null;
    }
    return document.getElementById(id);
  };

  /**
   * Focus the target after scroll without causing a native jump.
   *
   * @param {HTMLElement|null} target
   */
  const focusTarget = (target) => {
    if (!(target instanceof HTMLElement)) {
      return;
    }
    if (!target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
    }
    target.focus({ preventScroll: true });
  };

  /**
   * @param {string} hash
   * @param {boolean} updateHistory
   *
   * @return {boolean}
   *   Whether a scroll was started (hash was valid for this page).
   */
  const scrollToHash = (hash, updateHistory) => {
    const id = hash ? decodeURIComponent(hash.replace(/^#/, '')) : '';
    const target = resolveTarget(hash);

    if (id && !target) {
      return false;
    }

    if (updateHistory) {
      const next = id
        ? `#${id}`
        : window.location.pathname + window.location.search;
      if (window.location.hash !== (id ? `#${id}` : '')) {
        window.history.pushState(null, '', next);
      }
    }

    animateScrollTo(getTargetY(target), () => {
      focusTarget(target);
    });

    return true;
  };

  /**
   * Document/body hash navigation (load, hashchange, back/forward).
   *
   * @param {string} hash
   */
  const scrollBodyToHash = (hash) => {
    if (!hash) {
      return;
    }
    const target = resolveTarget(hash);
    if (!target) {
      return;
    }

    const targetY = getTargetY(target);
    const scrollY = lenis ? lenis.scroll : window.scrollY;
    if (scrollY > 1 && Math.abs(scrollY - targetY) < 50) {
      if (lenis) {
        lenis.scrollTo(0, { immediate: true, force: true });
      } else {
        window.scrollTo(window.scrollX, 0);
      }
    }

    window.requestAnimationFrame(() => {
      scrollToHash(hash, false);
    });
  };

  /**
   * @param {MouseEvent} event
   */
  const onDocumentClick = (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const link =
      event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement)) {
      return;
    }
    if (link.classList.contains('skip-link') || link.hasAttribute('download')) {
      return;
    }
    if (link.target && link.target !== '_self') {
      return;
    }

    let url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (e) {
      return;
    }

    if (
      url.origin !== window.location.origin ||
      normalizePath(url.pathname) !== normalizePath(window.location.pathname) ||
      url.search !== window.location.search
    ) {
      return;
    }

    const hrefAttr = link.getAttribute('href') || '';
    const hasFragment =
      url.hash !== '' || hrefAttr === '#' || hrefAttr.endsWith('#');
    if (!hasFragment) {
      return;
    }

    if (scrollToHash(url.hash, true)) {
      event.preventDefault();
    }
  };

  /**
   * Stop a running RAF animation when the user takes over (fallback path).
   *
   * @param {KeyboardEvent} [event]
   */
  const onUserScrollIntent = (event) => {
    if (event instanceof KeyboardEvent) {
      const keys = [
        'ArrowUp',
        'ArrowDown',
        'PageUp',
        'PageDown',
        'Home',
        'End',
        ' ',
      ];
      if (!keys.includes(event.key)) {
        return;
      }
    }
    cancelAnimation();
  };

  /**
   * Pause Lenis while a sheet locks body overflow.
   */
  const syncScrollLock = () => {
    if (!lenis) {
      return;
    }
    if (document.body.classList.contains(SCROLL_LOCK_CLASS)) {
      lenis.stop();
    } else {
      lenis.start();
    }
  };

  /**
   * Ease native scrollbar drags through Lenis.
   *
   * Lenis only interpolates wheel/touch input. Dragging the scrollbar fires
   * raw native `scroll` events that Lenis snaps to. We hold the page at
   * Lenis's eased position on each drag scroll and let Lenis animate toward
   * the dragged target instead.
   *
   * @param {InstanceType<typeof Lenis>} lenisInstance
   */
  const initScrollbarSmoothing = (lenisInstance) => {
    let easedScroll = lenisInstance.scroll;
    let dragging = false;
    let suppressSynthetic = false;

    lenisInstance.on('scroll', (instance) => {
      easedScroll = instance.scroll;
    });

    const inScrollbarGutter = (event) => {
      if (event.pointerType === 'touch') {
        return false;
      }
      const root = document.documentElement;
      return (
        event.clientX >= root.clientWidth || event.clientY >= root.clientHeight
      );
    };

    window.addEventListener('pointerdown', (event) => {
      if (inScrollbarGutter(event)) {
        dragging = true;
      }
    });

    const endDrag = () => {
      dragging = false;
    };
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    window.addEventListener(
      'scroll',
      () => {
        if (!dragging || suppressSynthetic || !lenisInstance) {
          return;
        }
        const target = window.scrollY;
        // Jump back to the eased position; the synthetic scroll from this
        // reset is ignored for one frame so we don't re-trigger smoothing.
        suppressSynthetic = true;
        window.scrollTo(window.scrollX, easedScroll);
        window.requestAnimationFrame(() => {
          suppressSynthetic = false;
        });
        lenisInstance.scrollTo(target, {
          force: true,
          lock: false,
          duration: 0.4,
        });
      },
      { passive: true },
    );
  };

  /**
   * @param {HTMLElement} html
   */
  const initLenis = (html) => {
    if (
      prefersReducedMotion() ||
      typeof Lenis !== 'function' ||
      !document.body.classList.contains('frontend--theme')
    ) {
      return;
    }

    lenis = new Lenis({
      // Continuous wheel/trackpad smoothing (lerp). Programmatic jumps pass
      // their own duration/easing via scrollTo options.
      lerp: 0.08,
      smoothWheel: true,
      syncTouch: false,
      autoRaf: true,
      anchors: false,
      allowNestedScroll: true,
      stopInertiaOnNavigate: true,
      respectReducedMotion: true,
    });

    Drupal.neo.lenis = lenis;
    html.classList.add('has-js-smooth-scroll');
    syncScrollLock();

    const observer = new MutationObserver(syncScrollLock);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });

    initScrollbarSmoothing(lenis);
  };

  /**
   * @param {HTMLElement} html
   */
  const init = (html) => {
    html.classList.add('has-js-anchor-scroll');
    initLenis(html);

    document.addEventListener('click', onDocumentClick);
    window.addEventListener('wheel', onUserScrollIntent, { passive: true });
    window.addEventListener('touchmove', onUserScrollIntent, { passive: true });
    window.addEventListener('keydown', onUserScrollIntent);
    window.addEventListener('hashchange', () => {
      scrollBodyToHash(window.location.hash);
    });
    window.addEventListener('popstate', () => {
      scrollBodyToHash(window.location.hash);
    });

    if (window.location.hash) {
      window.requestAnimationFrame(() => {
        scrollBodyToHash(window.location.hash);
      });
    }
  };

  Drupal.neo = Drupal.neo || {};
  Drupal.neo.animateScrollTo = animateScrollTo;

  Drupal.behaviors.anchorScroll = {
    attach(context) {
      once('anchor-scroll', 'html', context).forEach(init);
    },
  };
})(Drupal, once);
